import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { resumesApi } from '../../services/api';
import { Resume } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatBytes, formatDate } from '../../utils/format';
import { useToast } from '../../hooks/useToast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ModelSettings from '../../components/ai/ModelSettings';
import TailoredVersions from '../../components/ai/TailoredVersions';
import ExperienceLibrary from '../../components/ai/ExperienceLibrary';

const ACCEPT = '.pdf,.doc,.docx,.txt,.rtf,.odt';
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'];
const MAX_BYTES = 5 * 1024 * 1024;

const ResumesPage: React.FC = () => {
  const { show, toast } = useToast();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Rename dialog
  const [renaming, setRenaming] = useState<Resume | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // Delete dialog
  const [pendingDelete, setPendingDelete] = useState<Resume | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResumes(await resumesApi.list());
    } catch (e) {
      setError(errorMessage(e, 'Could not load your resumes.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetUpload = () => {
    setFile(null);
    setLabel('');
    setFileError(null);
    setProgress(0);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFileError(null);
    if (!f) {
      setFile(null);
      return;
    }
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_EXT.includes(ext)) {
      setFileError(`Unsupported file type ".${ext}". Allowed: ${ALLOWED_EXT.join(', ')}.`);
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setFileError(`File is ${formatBytes(f.size)}; the limit is ${formatBytes(MAX_BYTES)}.`);
      setFile(null);
      return;
    }
    setFile(f);
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const created = await resumesApi.upload(file, {
        label: label.trim() || undefined,
        onProgress: setProgress,
      });
      setResumes((prev) => {
        // First upload becomes default server-side; reflect whatever it says.
        const others = created.is_default ? prev.map((r) => ({ ...r, is_default: false })) : prev;
        return [created, ...others];
      });
      show('Resume uploaded.');
      setUploadOpen(false);
      resetUpload();
    } catch (e) {
      show(errorMessage(e, 'Upload failed.'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefault = async (resume: Resume) => {
    if (resume.is_default) return;
    setBusyId(resume.id);
    try {
      await resumesApi.patch(resume.id, { is_default: true });
      setResumes((prev) => prev.map((r) => ({ ...r, is_default: r.id === resume.id })));
      show(`"${resume.label || resume.filename}" is now your default resume.`);
    } catch (e) {
      show(errorMessage(e, 'Could not set the default resume.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = async () => {
    if (!renaming) return;
    setRenameSaving(true);
    try {
      const updated = await resumesApi.patch(renaming.id, { label: renameValue.trim() });
      setResumes((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      show('Resume renamed.');
      setRenaming(null);
    } catch (e) {
      show(errorMessage(e, 'Could not rename the resume.'), 'error');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await resumesApi.remove(pendingDelete.id);
      show('Resume deleted.');
      setPendingDelete(null);
      // The server may promote a new default; reload to reflect it.
      await load();
    } catch (e) {
      show(errorMessage(e, 'Could not delete the resume.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (resume: Resume) => {
    setBusyId(resume.id);
    try {
      await resumesApi.download(resume.id, resume.filename);
    } catch (e) {
      show(errorMessage(e, 'Could not download the resume.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
            Resumes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload the resumes you apply with. The default one is attached automatically.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Upload resume
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : resumes.length === 0 ? (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              No resumes yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a PDF or Word document up to {formatBytes(MAX_BYTES)}.
            </Typography>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>
              Upload your first resume
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {resumes.map((resume) => {
            const busy = busyId === resume.id;
            return (
              <Card key={resume.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <FileIcon color={resume.is_default ? 'primary' : 'action'} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {resume.label || resume.filename}
                        </Typography>
                        {resume.is_default && <Chip label="Default" size="small" color="primary" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {resume.filename} | {formatBytes(resume.size)} | uploaded {formatDate(resume.created_at)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={resume.is_default ? 'Default resume' : 'Set as default'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefault(resume)}
                            disabled={busy || resume.is_default}
                            color={resume.is_default ? 'primary' : 'default'}
                          >
                            {resume.is_default ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rename">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setRenaming(resume);
                              setRenameValue(resume.label);
                            }}
                            disabled={busy}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download">
                        <span>
                          <IconButton size="small" onClick={() => handleDownload(resume)} disabled={busy}>
                            {busy ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPendingDelete(resume)}
                            disabled={busy}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {!loading && !error && (
        <Box sx={{ mt: 3 }}>
          <ExperienceLibrary notify={show} />
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ mt: 3 }}>
          <TailoredVersions notify={show} />
          <Box sx={{ mt: 3 }}>
            <ModelSettings notify={show} reloadKey={resumes.map((r) => `${r.id}:${r.is_default}`).join(',')} />
          </Box>
        </Box>
      )}

      {/* Upload dialog */}
      <Dialog
        open={uploadOpen}
        onClose={() => {
          if (uploading) return;
          setUploadOpen(false);
          resetUpload();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload resume</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />} disabled={uploading}>
              {file ? file.name : 'Choose file'}
              <input ref={fileInput} type="file" hidden accept={ACCEPT} onChange={handleFileChange} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Accepted: {ALLOWED_EXT.map((e) => `.${e}`).join(', ')}. Max {formatBytes(MAX_BYTES)}.
            </Typography>
            {fileError && <Alert severity="error">{fileError}</Alert>}
            <TextField
              label="Label"
              placeholder="e.g. General, Frontend, Data"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
              disabled={uploading}
              helperText="A short name so you can tell your resumes apart."
            />
            {uploading && <LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadOpen(false);
              resetUpload();
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={Boolean(renaming)} onClose={() => !renameSaving && setRenaming(null)} fullWidth maxWidth="xs">
        <DialogTitle>Rename resume</DialogTitle>
        <DialogContent>
          <TextField
            label="Label"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)} disabled={renameSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRename} disabled={renameSaving || !renameValue.trim()}>
            {renameSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this resume?"
        message={
          pendingDelete
            ? `"${pendingDelete.label || pendingDelete.filename}" will be permanently deleted. Applications that used it will keep their record but lose the link.`
            : undefined
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {toast}
    </Container>
  );
};

export default ResumesPage;
